// TrackedBox2.tsx
import React, { useEffect, forwardRef } from 'react';
import { Box as MuiBox, BoxProps, useTheme, useMediaQuery, Breakpoint } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { api } from '../../api/analiticsApi';

// --- Types & Interfaces for Client Hints ---
interface UADataValues {
  brands?: { brand: string; version: string }[];
  mobile?: boolean;
  platform?: string;
  architecture?: string;
  bitness?: string;
  model?: string;
  platformVersion?: string;
  fullVersionList?: { brand: string; version: string }[];
}

interface NavigatorUAData extends UADataValues {
  getHighEntropyValues(hints: string[]): Promise<UADataValues>;
}

interface NavigatorWithUA extends Navigator {
  userAgentData?: NavigatorUAData;
}

// --- Helper Functions ---

export function useCurrentBreakpoint(): Breakpoint {
  const theme = useTheme();
  const isXl = useMediaQuery(theme.breakpoints.up('xl'));
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));
  const isMd = useMediaQuery(theme.breakpoints.up('md'));
  const isSm = useMediaQuery(theme.breakpoints.up('sm'));

  if (isXl) return 'xl';
  if (isLg) return 'lg';
  if (isMd) return 'md';
  if (isSm) return 'sm';
  return 'xs';
}

/**
 * Асинхронный сбор данных браузера
 */
async function collectClientHints() {
  const nav = navigator as NavigatorWithUA;
  const res: any = {
    userAgent: nav.userAgent,
    language: nav.language,
    platform: nav.platform,
  };

  if (nav.userAgentData) {
    try {
      const highEntropy = await nav.userAgentData.getHighEntropyValues([
        'architecture',
        'bitness',
        'model',
        'platformVersion',
        'fullVersionList',
      ]);
      
      // Превращаем массивы брендов в читаемые строки для БД
      const parseBrands = (list?: { brand: string; version: string }[]) => 
        list?.map(b => `${b.brand} (v${b.version})`).join(', ');

      return {
        ...res,
        browserBrands: parseBrands(nav.userAgentData.brands),
        browserFullVersions: parseBrands(highEntropy.fullVersionList),
        isMobile: nav.userAgentData.mobile,
        osPlatform: nav.userAgentData.platform,
        osVersion: highEntropy.platformVersion,
        arch: highEntropy.architecture,
        model: highEntropy.model,
        bitness: highEntropy.bitness,
      };
    } catch (e) {
      console.warn("UA-CH failed", e);
    }
  }
  return res;
}

// --- Main Component ---

export const TrackedBox = forwardRef<HTMLDivElement, BoxProps>(
  ({ children, ...props }, ref) => {
    const location = useLocation();
    const currentBreakpoint = useCurrentBreakpoint();
    const auth = useAuth();

    useEffect(() => {
      if (!auth.userId) return;

      const pagePath = location.pathname;
      const pageName = pagePath === '/' 
        ? 'Home' 
        : pagePath.substring(1).charAt(0).toUpperCase() + pagePath.slice(2);

      const logAnalytics = async () => {
        const hints = await collectClientHints();

        const payload = {
          pagePath,
          pageName,
          // Размеры экрана
          screenWidth: window.screen.width,
          screenHeight: window.screen.height,
          windowWidth: window.innerWidth,
          windowHeight: window.innerHeight,
          devicePixelRatio: window.devicePixelRatio,
          currentBreakpoint,
          // Данные из Client Hints + Legacy
          ...hints
        };

        try {
          await api.analiticsPost(
            'LPR-DJ',
            'live',
            auth.userId || 0,
            payload
          );
          console.log(`[Analytics] Tracked: ${pageName}`, payload);
        } catch (error) {
          console.error("[Analytics] Error sending data:", error);
        }
      };

      logAnalytics();
    }, [location.pathname, auth?.userId]); // Срабатывает при смене страницы или логине

    return (
      <MuiBox ref={ref} {...props}>
        {children}
      </MuiBox>
    );
  }
);